import {isFeatureEnabled, isFeatureEnabledWithOptOut} from '@github-ui/feature-flags'
import type {GlobalAgentsMenuWithAnchorProps} from '@github-ui/global-agents-menu/GlobalAgentsMenu'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {AgentIcon, TriangleDownIcon} from '@primer/octicons-react'
import {Button, IconButton, Spinner} from '@primer/react'
import {
  lazy,
  type MouseEventHandler,
  type RefObject,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import {useDeferredPayloadSlice} from '../utils/DeferredNavPayloadContext'

export type LazyGlobalNavAgentsMenuProps = Pick<
  GlobalAgentsMenuWithAnchorProps,
  'anchorProps' | 'copilotApiUrl' | 'isOneCopilotNav' | 'showRelaunchAnnouncement'
> & {onFreePopoverSettled?: () => void}

export function LazyGlobalNavAgentsMenu({
  anchorProps,
  copilotApiUrl,
  isOneCopilotNav,
  showRelaunchAnnouncement,
  onFreePopoverSettled,
}: LazyGlobalNavAgentsMenuProps) {
  const {isBrowser} = useRuntimeEnvironment()
  const [shouldLoad, setShouldLoad] = useState(true)
  const [openOnLoad, setOpenOnLoad] = useState(false)
  const [agentsMenuLoaded, setAgentsMenuLoaded] = useState(false)
  const anchorRef = useRef<HTMLButtonElement | null>(null)
  const focusedFallbackRef = useRef(false)
  const {data: agentsPanelProps, isPending} = useDeferredPayloadSlice('agents_menu')
  const chatEnabled = isFeatureEnabledWithOptOut('one_copilot_chat', 'one_copilot_chat_ui_opt_out')
  const gatedRelaunchAnnouncement = showRelaunchAnnouncement === true && chatEnabled
  const waitForOnboardingPayload =
    gatedRelaunchAnnouncement || (chatEnabled && isFeatureEnabled('one_copilot_environment_strip_tour'))

  const buttonId = agentsPanelProps?.agentsButtonId ?? 'global-copilot-agent-button'

  useEffect(() => {
    if (!isBrowser || !shouldLoad || !waitForOnboardingPayload) return
    let cancelled = false
    async function preload() {
      await Promise.allSettled([loadGlobalAgentsMenu()])
      if (!cancelled) setAgentsMenuLoaded(true)
    }
    void preload()
    return () => {
      cancelled = true
    }
  }, [isBrowser, shouldLoad, waitForOnboardingPayload])

  const handleIntentToOpen = useCallback(() => {
    setShouldLoad(true)
  }, [])

  const handleClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    event => {
      setShouldLoad(true)
      setOpenOnLoad(true)
      anchorProps?.onClick?.(event)
    },
    [anchorProps],
  )

  if (!isBrowser || !shouldLoad) {
    return (
      <AgentsButtonFallback
        anchorProps={anchorProps}
        loading={false}
        openOnLoad={false}
        handleIntentToOpen={handleIntentToOpen}
        handleClick={handleClick}
        buttonId={buttonId}
        isOneCopilotNav={isOneCopilotNav}
      />
    )
  }

  const fallback = (
    <AgentsButtonFallback
      anchorProps={anchorProps}
      loading={isPending}
      openOnLoad={openOnLoad}
      handleIntentToOpen={handleIntentToOpen}
      handleClick={handleClick}
      buttonId={buttonId}
      isOneCopilotNav={isOneCopilotNav}
      focusedFallbackRef={waitForOnboardingPayload ? focusedFallbackRef : undefined}
    />
  )
  const menu = (
    <ResolvedGlobalAgentsMenu
      {...agentsPanelProps}
      onFreePopoverSettled={!isPending ? onFreePopoverSettled : undefined}
      copilotApiUrl={copilotApiUrl}
      isOneCopilotNav={isOneCopilotNav}
      showRelaunchAnnouncement={gatedRelaunchAnnouncement}
      open={openOnLoad && !isPending}
      setOpen={setOpenOnLoad}
      anchorRef={anchorRef}
      focusedFallbackRef={waitForOnboardingPayload ? focusedFallbackRef : undefined}
      anchorProps={{
        ...anchorProps,
        id: buttonId,
        onClick: event => {
          anchorProps?.onClick?.(event)
        },
      }}
    />
  )
  return (
    <Suspense fallback={fallback}>
      {waitForOnboardingPayload && (isPending || !agentsMenuLoaded) ? fallback : menu}
    </Suspense>
  )
}

function ResolvedGlobalAgentsMenu({
  onFreePopoverSettled,
  focusedFallbackRef,
  ...props
}: GlobalAgentsMenuWithAnchorProps & {onFreePopoverSettled?: () => void} & {
  open: boolean
  setOpen: (open: boolean) => void
  anchorRef: React.RefObject<HTMLButtonElement | null>
  focusedFallbackRef?: RefObject<boolean>
}) {
  useLayoutEffect(() => {
    if (focusedFallbackRef?.current && props.anchorRef.current) {
      props.anchorRef.current.focus()
      focusedFallbackRef.current = false
    }
  }, [focusedFallbackRef, props.anchorRef])

  useEffect(() => {
    onFreePopoverSettled?.()
  }, [onFreePopoverSettled])

  return <LazyGlobalAgentsMenuWithAnchor {...props} />
}

function AgentsButtonFallback({
  loading,
  openOnLoad,
  handleIntentToOpen,
  handleClick,
  buttonId,
  anchorProps,
  isOneCopilotNav,
  focusedFallbackRef,
}: {
  loading: boolean
  openOnLoad?: boolean
  handleIntentToOpen: () => void
  handleClick: MouseEventHandler<HTMLButtonElement>
  buttonId: string
  anchorProps?: GlobalAgentsMenuWithAnchorProps['anchorProps']
  isOneCopilotNav?: boolean
  focusedFallbackRef?: RefObject<boolean>
}) {
  const buttonRef = useRef<HTMLButtonElement>(null)
  useLayoutEffect(() => {
    const button = buttonRef.current
    if (focusedFallbackRef?.current) button?.focus()
    return () => {
      if (focusedFallbackRef && button) focusedFallbackRef.current = button === button.ownerDocument.activeElement
    }
  }, [focusedFallbackRef])

  // only show the spinner if we're waiting to show the dialog
  const shouldShowLoading = loading && openOnLoad
  if (isOneCopilotNav) {
    return (
      <IconButton
        ref={focusedFallbackRef ? buttonRef : undefined}
        id={buttonId}
        variant={anchorProps?.variant ?? 'invisible'}
        className={anchorProps?.className}
        icon={shouldShowLoading ? () => <Spinner size="small" /> : TriangleDownIcon}
        aria-label="Open Copilot menu"
        aria-haspopup="dialog"
        onMouseEnter={handleIntentToOpen}
        onFocus={handleIntentToOpen}
        onClick={handleClick}
      />
    )
  }

  return (
    <Button
      ref={focusedFallbackRef ? buttonRef : undefined}
      id={buttonId}
      variant={anchorProps?.variant ?? 'invisible'}
      className={anchorProps?.className}
      leadingVisual={AgentIcon}
      trailingVisual={shouldShowLoading ? () => <Spinner size="small" /> : TriangleDownIcon}
      aria-label="Open agents panel"
      onMouseEnter={handleIntentToOpen}
      onFocus={handleIntentToOpen}
      onClick={handleClick}
    >
      {''}
    </Button>
  )
}

type AgentsMenuModule = {
  default: typeof import('@github-ui/global-agents-menu/GlobalAgentsMenu').GlobalAgentsMenuWithAnchor
}
let agentsMenuImport: Promise<AgentsMenuModule> | undefined

function loadGlobalAgentsMenu(): Promise<AgentsMenuModule> {
  agentsMenuImport ??= (async () => {
    const mod = await import('@github-ui/global-agents-menu/GlobalAgentsMenu')
    return {default: mod.GlobalAgentsMenuWithAnchor}
  })()
  return agentsMenuImport
}

const LazyGlobalAgentsMenuWithAnchor = lazy(loadGlobalAgentsMenu)
